import { getEnrollment } from './intake.js'

export const siteConfig = {
  brand: {
    academyName: 'Skillo Academy',
    tagline:
      'Live AI Agent, Web Development, and practical tech skill courses from Sylhet, Bangladesh.',
    shortTagline: 'AI, Web Development, and practical career skills from Sylhet.',
    primarySearchIdentity:
      'AI Agent, Web Development & Practical Tech Courses in Sylhet, Bangladesh',
    entityStatement:
      'Skillo Academy is a live mentor-led tech academy in Sylhet, Bangladesh, teaching AI Agent Building, Full Stack Web Development with AI, practical digital skills, and foundation computer/English skills in Bangla + English through small cohort classes, real projects, portfolio building, and honest career guidance. Freelancing is one optional path — not a guaranteed outcome.',
  },
  contact: {
    phoneDisplay: '01781583107',
    phoneE164: '8801781583107',
    email: 'dm@minhajsadik.com',
    location: 'Tilagor, Sylhet',
    addressLocality: 'Sylhet',
    addressRegion: 'Sylhet Division',
    addressCountry: 'BD',
  },
  pricing: {
    currency: '৳',
    earlyBird: '৳11,999',
    regular: '৳14,999',
    range: '৳2,999 – ৳19,999',
  },
  campaign: {
    name: 'Skillo Career Launch Batch',
    path: '/career-launch',
    mainCta: 'Free Live Career Class + Counselling',
    leadKeywords: {
      ai: 'AI',
      web: 'WEB',
      aiClass: 'AI CLASS',
    },
    targetCourseSlugs: [
      'ai-agent-builder-beginner-to-pro',
      'full-stack-ai-web-development',
      'react-native-ai',
    ],
    orientationPath: '/free-career-orientation',
    aiRoadmapPath: '/roadmaps/ai-agent-builder-90-day-roadmap',
    webRoadmapPath: '/roadmaps/full-stack-developer-24-week-roadmap',
    legacyWebRoadmapPath: '/roadmaps/full-stack-developer-12-week-roadmap',
    referralScholarshipPath: '/admissions/referral-scholarship',
    seatBookingToken: '৳2,000',
    installment: '50% before class start, 50% by Week 4',
    referral: {
      referrerReward: '৳1,500 course credit / bKash reward',
      referredStudentDiscount: '৳1,000 admission discount',
      condition: 'Reward is confirmed after the referred student pays minimum 50%.',
    },
    scholarship: {
      seatsPerCourse: '2 partial scholarships per flagship course',
      types: ['1 merit-based scholarship', '1 financial-need based scholarship'],
      requirements: [
        'Submit a 1-minute video explaining why you want to join.',
        'Keep at least 90% class attendance if selected.',
      ],
    },
  },
  social: {
    facebookUrl: 'https://facebook.com',
    founderWebsite: 'https://minhajsadik.com',
    founderGithub: 'https://github.com/MinhajSadik',
    founderLinkedIn: 'https://linkedin.com/in/minhajosadik',
    founderX: 'https://x.com/MinhajSadik1',
    founderYoutube: 'https://youtube.com/@minhajsadik',
  },
  seo: {
    baseUrl: 'https://skillo.minhajsadik.com',
    founderPersonId: 'https://minhajsadik.com/#person',
    organizationId: 'https://skillo.minhajsadik.com/#organization',
    websiteId: 'https://skillo.minhajsadik.com/#website',
    language: ['bn', 'en'],
    crawlerPolicy: [
      { agent: 'Googlebot', allow: true, note: 'Google Search' },
      { agent: 'Bingbot', allow: true, note: 'Bing Search' },
      { agent: 'GPTBot', allow: true, note: 'OpenAI training crawler' },
      { agent: 'ChatGPT-User', allow: true, note: 'ChatGPT browse mode' },
      { agent: 'OAI-SearchBot', allow: true, note: 'ChatGPT Search' },
      { agent: 'ClaudeBot', allow: true, note: 'Anthropic training crawler' },
      { agent: 'Claude-Web', allow: true, note: 'Claude browse mode' },
      { agent: 'anthropic-ai', allow: true, note: 'Anthropic legacy ID' },
      { agent: 'Claude-SearchBot', allow: true, note: 'Claude Search' },
      { agent: 'PerplexityBot', allow: true, note: 'Perplexity training/index' },
      { agent: 'Perplexity-User', allow: true, note: 'Perplexity browse mode' },
      { agent: 'Google-Extended', allow: true, note: 'Gemini training + AI Overviews' },
      { agent: 'Applebot', allow: true, note: 'Apple search index' },
      { agent: 'Applebot-Extended', allow: true, note: 'Apple Intelligence training' },
      { agent: 'Amazonbot', allow: true },
      { agent: 'CCBot', allow: true, note: 'Common Crawl feeds many open-weight LLMs' },
      { agent: 'Meta-ExternalAgent', allow: true, note: 'Meta / Llama training' },
      { agent: 'FacebookBot', allow: true },
      { agent: 'MistralAI-User', allow: true, note: 'Mistral browse mode' },
      { agent: 'DuckAssistBot', allow: true, note: 'DuckDuckGo AI' },
      { agent: 'Bytespider', allow: false, note: 'TikTok scraper - block' },
    ],
  },
  trust: {
    incomeDisclaimer:
      'Skillo Academy does not guarantee freelancing income, job placement, or fixed earnings. We provide practical training, projects, portfolio guidance, and career support. Student results depend on practice, communication, consistency, market demand, and individual effort.',
  },
} as const

export function buildWhatsAppUrl(
  message?: string,
  phoneDigits: string = siteConfig.contact.phoneE164,
) {
  const base = `https://wa.me/${phoneDigits}`
  if (!message) {
    return base
  }

  return `${base}?text=${encodeURIComponent(message)}`
}

export function buildCampaignWhatsAppUrl(
  keyword: keyof typeof siteConfig.campaign.leadKeywords,
  now = new Date(),
  options?: { includeIntake?: boolean },
) {
  const leadKeyword = siteConfig.campaign.leadKeywords[keyword]
  const includeIntake = options?.includeIntake !== false
  const enrollment = getEnrollment(now)
  const message = includeIntake
    ? `Hi Skillo Academy, I want the ${leadKeyword} roadmap for ${siteConfig.campaign.name} (${enrollment.batchMonth} ${enrollment.batchYear}).`
    : `Hi Skillo Academy, I want the ${leadKeyword} roadmap for ${siteConfig.campaign.name}.`
  return buildWhatsAppUrl(message)
}

export function buildCounsellingWhatsAppUrl(now = new Date()) {
  return buildWhatsAppUrl(
    `Hi Skillo Academy, I want counselling for ${getEnrollment(now).batchLabel}.`,
  )
}
